import React, { useState } from 'react';
import { useTransactions } from '../contexts/TransactionContext';
import { useAccounts } from '../contexts/AccountContext';
import { useCurrency } from '../contexts/CurrencyContext';
import { useTheme } from '../contexts/ThemeContext';
import { X, Sparkles } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { format } from 'date-fns';
import ReceiptScannerModal from './ReceiptScannerModal';

const EXPENSE_CATEGORIES = [
  { name: 'Food & Dining', icon: '🍔' },
  { name: 'Transportation', icon: '🚗' },
  { name: 'Housing & Utilities', icon: '🏠' },
  { name: 'Groceries', icon: '🛒' },
  { name: 'Entertainment', icon: '🎬' },
  { name: 'Healthcare', icon: '💊' },
  { name: 'Shopping', icon: '👕' },
  { name: 'Education', icon: '📚' },
  { name: 'Travel', icon: '✈️' },
  { name: 'Bills & Fees', icon: '💳' },
  { name: 'Gifts', icon: '🎁' },
  { name: 'Other', icon: '📱' },
];

const INCOME_CATEGORIES = [
  { name: 'Salary', icon: '💰' },
  { name: 'Freelance', icon: '💼' },
  { name: 'Gift', icon: '🎁' },
  { name: 'Investment', icon: '📈' },
  { name: 'Other Income', icon: '💵' },
];

const AddTransactionModal = ({ isOpen, onClose, editTransaction = null }) => {
  const { addTransaction, updateTransaction } = useTransactions();
  const { accounts } = useAccounts();
  const { displayCurrency, getCurrencyInfo } = useCurrency();
  const { theme, isDark } = useTheme();
  const currencyInfo = getCurrencyInfo(displayCurrency);
  const [type, setType] = useState('expense');
  const [amount, setAmount] = useState('');
  const [category, setCategory] = useState('');
  const [account, setAccount] = useState('');
  const [toAccount, setToAccount] = useState('');
  const [date, setDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [description, setDescription] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [isScannerOpen, setIsScannerOpen] = useState(false);

  const handleApplyReceipt = ({ amount: recAmount, description: recDesc, category: recCat, date: recDate }) => {
    setType('expense');
    if (recAmount) setAmount(recAmount);
    if (recDesc) setDescription(recDesc);
    if (recCat) setCategory(recCat);
    if (recDate) setDate(recDate);
  };

  const categories = type === 'expense' ? EXPENSE_CATEGORIES : (type === 'income' ? INCOME_CATEGORIES : [{ name: 'Transfer', icon: '🔄' }]);

  // Set default account when accounts load
  React.useEffect(() => {
    if (accounts.length > 0 && !account && !editTransaction) {
      setAccount(accounts[0].name);
    }
  }, [accounts, account, editTransaction]);

  // Pre-fill form when editing
  React.useEffect(() => {
    if (editTransaction) {
      setType(editTransaction.type);
      setAmount(editTransaction.amount.toString());
      setCategory(editTransaction.category);
      setAccount(editTransaction.account);
      setToAccount(editTransaction.toAccount || '');
      setDate(format(editTransaction.date, 'yyyy-MM-dd'));
      setDescription(editTransaction.description || '');
    } else {
      setType('expense');
      setAmount('');
      setCategory('Food & Dining');
      setAccount(accounts.length > 0 ? accounts[0].name : '');
      setToAccount('');
      setDate(format(new Date(), 'yyyy-MM-dd'));
      setDescription('');
    }
  }, [editTransaction, accounts]);

  // Set default category when type changes (only for new transactions)
  React.useEffect(() => {
    if (!editTransaction) {
      if (type === 'expense') {
        setCategory('Food & Dining');
      } else if (type === 'income') {
        setCategory('Salary');
      } else {
        setCategory('Transfer');
      }
    }
  }, [type, editTransaction]);

  const handleSave = async () => {
    // Validation
    if (!amount || parseFloat(amount) <= 0) {
      setError('Please enter a valid amount');
      return;
    }
    
    if (type !== 'transfer' && !category) {
      setError('Please select a category');
      return;
    }
    
    if (!account) {
      setError('Please select an account');
      return;
    }

    if (type === 'transfer' && (!toAccount || toAccount === account)) {
      setError('Please select a different destination account');
      return;
    }

    setError('');
    setSaving(true);

    try {
      const categoryData = categories.find(c => c.name === category);
      const transactionData = {
        type,
        amount: parseFloat(amount),
        category: type === 'transfer' ? 'Transfer' : category,
        categoryIcon: type === 'transfer' ? '🔄' : (categoryData?.icon || '📱'),
        account,
        ...(type === 'transfer' ? { toAccount } : {}),
        date: new Date(date),
        description: description.trim()
      };

      if (editTransaction) {
        await updateTransaction(editTransaction.id, transactionData);
        toast.success('Transaction updated!');
      } else {
        await addTransaction(transactionData);
        toast.success('Transaction added!');
      }
      
      // Reset form
      setAmount('');
      setCategory(type === 'expense' ? 'Food & Dining' : 'Salary');
      setAccount(accounts.length > 0 ? accounts[0].name : '');
      setToAccount('');
      setDate(format(new Date(), 'yyyy-MM-dd'));
      setDescription('');
      
      onClose();
    } catch (error) {
      console.error('Failed to save transaction:', error);
      setError('Failed to save. Please try again.');
      toast.error(editTransaction ? 'Failed to update transaction' : 'Failed to save transaction');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setAmount('');
    setCategory(type === 'expense' ? 'Food & Dining' : 'Salary');
    setAccount(accounts.length > 0 ? accounts[0].name : '');
    setToAccount('');
    setDate(format(new Date(), 'yyyy-MM-dd'));
    setDescription('');
    setError('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      <div 
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.6)' }}
      onClick={handleCancel}
    >
      <div 
        className="rounded-xl w-full max-w-md p-6 animate-in fade-in duration-200"
        style={{ 
          backgroundColor: theme.cardBackground,
          boxShadow: isDark ? '0 8px 32px rgba(0, 0, 0, 0.5)' : '0 8px 32px rgba(0, 0, 0, 0.15)',
          border: `1px solid ${theme.divider}`
        }}
        onClick={(e) => e.stopPropagation()}
        data-testid="add-transaction-modal"
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-bold" style={{ color: theme.text }}>
            {editTransaction ? 'Edit Transaction' : 'Add Transaction'}
          </h2>
          <div className="flex items-center gap-2">
            {!editTransaction && (
              <button
                type="button"
                onClick={() => setIsScannerOpen(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all hover:scale-105 shadow-sm"
                style={{
                  backgroundColor: `${theme.accent}15`,
                  color: theme.accent,
                  border: `1px solid ${theme.accent}40`,
                }}
                title="Scan receipt image to auto-fill"
              >
                <Sparkles className="w-3.5 h-3.5" />
                Scan Receipt
              </button>
            )}
            <button
              onClick={handleCancel}
              className="p-1 rounded-full transition-colors"
              style={{ color: theme.textSecondary }}
              data-testid="close-modal-button"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Type Toggle */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setType('expense')}
            className="flex-1 py-2 px-3 rounded-lg font-semibold transition-all duration-200 text-sm md:text-base"
            style={{
              backgroundColor: type === 'expense' ? theme.accent : (isDark ? theme.inputBackground : '#F1F3F4'),
              color: type === 'expense' ? 'white' : theme.textSecondary,
              border: 'none'
            }}
            data-testid="expense-type-button"
          >
            Expense
          </button>
          <button
            onClick={() => setType('income')}
            className="flex-1 py-2 px-3 rounded-lg font-semibold transition-all duration-200 text-sm md:text-base"
            style={{
              backgroundColor: type === 'income' ? theme.accentBlue : (isDark ? theme.inputBackground : '#F1F3F4'),
              color: type === 'income' ? 'white' : theme.textSecondary,
              border: 'none'
            }}
            data-testid="income-type-button"
          >
            Income
          </button>
          <button
            onClick={() => setType('transfer')}
            className="flex-1 py-2 px-3 rounded-lg font-semibold transition-all duration-200 text-sm md:text-base"
            style={{
              backgroundColor: type === 'transfer' ? '#9C27B0' : (isDark ? theme.inputBackground : '#F1F3F4'),
              color: type === 'transfer' ? 'white' : theme.textSecondary,
              border: 'none'
            }}
            data-testid="transfer-type-button"
          >
            Transfer
          </button>
        </div>

        {/* Amount Input */}
        <div className="mb-6">
          <Label className="text-sm font-medium mb-2 block" style={{ color: theme.textSecondary }}>
            Amount ({currencyInfo.code})
          </Label>
          <div className="relative">
            <span 
              className="absolute left-4 top-1/2 -translate-y-1/2 text-2xl font-bold" 
              style={{ color: theme.text }}
            >
              {currencyInfo.symbol}
            </span>
            <Input
              type="number"
              step="0.01"
              placeholder="0.00"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="pl-10 text-2xl font-bold h-14"
              style={{ 
                borderColor: theme.divider, 
                backgroundColor: isDark ? theme.inputBackground : 'white',
                color: theme.text,
                borderRadius: '8px' 
              }}
              autoFocus
              data-testid="amount-input"
            />
          </div>
        </div>

        {/* Category Select (Only if not transfer) */}
        {type !== 'transfer' && (
          <div className="mb-6">
            <Label className="text-sm font-medium mb-2 block" style={{ color: theme.textSecondary }}>
              Category
            </Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger 
                className="h-12"
                style={{ 
                  borderColor: theme.divider, 
                  backgroundColor: isDark ? theme.inputBackground : 'white',
                  color: theme.text,
                  borderRadius: '8px' 
                }}
                data-testid="category-select"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent style={{ backgroundColor: theme.cardBackground, borderColor: theme.divider }}>
                {categories.map((cat) => (
                  <SelectItem key={cat.name} value={cat.name} style={{ color: theme.text }}>
                    <span className="flex items-center gap-2">
                      <span>{cat.icon}</span>
                      <span>{cat.name}</span>
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Account Select */}
        <div className="mb-6">
          <Label className="text-sm font-medium mb-2 block" style={{ color: theme.textSecondary }}>
            {type === 'transfer' ? 'From Account' : 'Account'}
          </Label>
          {accounts.length === 0 ? (
            <div className="text-sm p-3 rounded-lg" style={{ backgroundColor: isDark ? '#3D2D10' : '#FEF3C7', color: isDark ? '#FCD34D' : '#92400E' }}>
              No accounts available. Please create an account in the Accounts section first.
            </div>
          ) : (
            <Select value={account} onValueChange={setAccount}>
              <SelectTrigger 
                className="h-12"
                style={{ 
                  borderColor: theme.divider, 
                  backgroundColor: isDark ? theme.inputBackground : 'white',
                  color: theme.text,
                  borderRadius: '8px' 
                }}
                data-testid="account-select"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent style={{ backgroundColor: theme.cardBackground, borderColor: theme.divider }}>
                {accounts.map((acc) => (
                  <SelectItem key={acc.id} value={acc.name} style={{ color: theme.text }}>
                    {acc.type === 'cash' && '💰 '}
                    {acc.type === 'bank' && '🏦 '}
                    {acc.type === 'card' && '💳 '}
                    {acc.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>

        {/* Destination Account (Only for Transfer) */}
        {type === 'transfer' && (
          <div className="mb-6">
            <Label className="text-sm font-medium mb-2 block" style={{ color: theme.textSecondary }}>
              To Account
            </Label>
            <Select value={toAccount} onValueChange={setToAccount}>
              <SelectTrigger 
                className="h-12"
                style={{ 
                  borderColor: theme.divider, 
                  backgroundColor: isDark ? theme.inputBackground : 'white',
                  color: theme.text,
                  borderRadius: '8px' 
                }}
                data-testid="to-account-select"
              >
                <SelectValue placeholder="Select destination account" />
              </SelectTrigger>
              <SelectContent style={{ backgroundColor: theme.cardBackground, borderColor: theme.divider }}>
                {accounts.filter(a => a.name !== account).map((acc) => (
                  <SelectItem key={acc.id} value={acc.name} style={{ color: theme.text }}>
                    {acc.type === 'cash' && '💰 '}
                    {acc.type === 'bank' && '🏦 '}
                    {acc.type === 'card' && '💳 '}
                    {acc.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Date Picker */}
        <div className="mb-6">
          <Label className="text-sm font-medium mb-2 block" style={{ color: theme.textSecondary }}>
            Date
          </Label>
          <Input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="h-12"
            style={{ 
              borderColor: theme.divider, 
              backgroundColor: isDark ? theme.inputBackground : 'white',
              color: theme.text,
              borderRadius: '8px' 
            }}
            data-testid="date-input"
          />
        </div>

        {/* Description Input */}
        <div className="mb-6">
          <Label className="text-sm font-medium mb-2 block" style={{ color: theme.textSecondary }}>
            Description (optional)
          </Label>
          <Input
            type="text"
            placeholder="Add a note..."
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            maxLength={100}
            className="h-12"
            style={{ 
              borderColor: theme.divider, 
              backgroundColor: isDark ? theme.inputBackground : 'white',
              color: theme.text,
              borderRadius: '8px' 
            }}
            data-testid="description-input"
          />
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-4 text-sm font-medium" style={{ color: theme.accent }}>
            {error}
          </div>
        )}

        {/* Buttons */}
        <div className="flex gap-3">
          <Button
            onClick={handleCancel}
            variant="outline"
            className="flex-1 h-12"
            style={{ 
              borderColor: theme.divider, 
              borderRadius: '8px',
              color: theme.text
            }}
            data-testid="cancel-button"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving || accounts.length === 0}
            className="flex-1 h-12 text-white font-medium"
            style={{ 
              backgroundColor: theme.accent,
              borderRadius: '8px',
              opacity: accounts.length === 0 ? 0.5 : 1
            }}
            data-testid="save-button"
          >
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </div>
    </div>

    {/* OCR Receipt Scanner Modal */}
    <ReceiptScannerModal
      isOpen={isScannerOpen}
      onClose={() => setIsScannerOpen(false)}
      onApplyReceipt={handleApplyReceipt}
    />
  </>
);
};

export default AddTransactionModal;
